// /dashboard/forbidden — landing page for routes the current role can't enter.
//
// Linked from RequireRole when redirectTo defaults. Carries query hints
// (?from=<role>&missing=<scope>) so the operator can see what was missing.
//
// Special case: `session.role === "guest"` means「未登入」 not「角色不對」.
// New tenant_admin / manager invited via LINE binding lands here too —
// the LIFF redeem only writes membership to D1; the dashboard origin
// has no nexus_session cookie until they ALSO sign in via /login at the
// dashboard. Showing「請聯絡您的單位管理員」 in that case is wrong: the
// fix is for THEM to click 登入 + go through LINE OAuth on this origin.
// We branch the copy so guests see「您尚未登入」 with a primary login
// CTA preserving return_to.

import React from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { ShieldAlert, LogIn } from "lucide-react";
import { useDashboardSession } from "../auth/useDashboardSession.js";

export default function Forbidden(): React.ReactElement {
  const [params] = useSearchParams();
  const session = useDashboardSession();
  const location = useLocation();
  const fromRole = params.get("from");
  const missingScope = params.get("missing");
  const isGuest = session.role === "guest";

  // Preserve where the operator was trying to go so /login can bounce
  // them back after auth.
  const loginHref = `/login?return_to=${encodeURIComponent(location.pathname || "/dashboard/")}`;

  return (
    <div className="max-w-xl mx-auto rounded-2xl bg-bg-surface border border-border p-8 mt-12 shadow-xl">
      <div className="flex items-center gap-3 mb-4 text-warning">
        <ShieldAlert className="h-5 w-5" strokeWidth={2.25} />
        <span className="text-xs font-mono uppercase tracking-wider">
          {isGuest ? "未登入" : "無權限"}
        </span>
      </div>
      <h1 className="text-xl font-semibold text-text-primary">
        {isGuest ? "請先登入" : "此頁面您目前無法瀏覽"}
      </h1>
      {isGuest ? (
        <p className="mt-4 text-sm text-text-secondary leading-relaxed">
          您目前以訪客身分進入。若您已透過 LINE 完成邀請碼綁定，請點「立即登入」用同一個
          LINE 帳號完成後台登入；登入完成後會自動回到您要瀏覽的頁面。
        </p>
      ) : (
        <>
          <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-text-muted">您的身分</dt><dd className="font-mono">{session.role}</dd>
            <dt className="text-text-muted">所屬組織</dt><dd className="font-mono">{session.tenant_id || "—"}</dd>
            {fromRole && (<>
              <dt className="text-text-muted">原本身分</dt><dd className="font-mono">{fromRole}</dd>
            </>)}
            {missingScope && (<>
              <dt className="text-text-muted">缺少權限</dt><dd className="font-mono text-danger">{missingScope}</dd>
            </>)}
          </dl>
          <p className="mt-5 text-sm text-text-secondary">
            若您認為這是錯誤,請聯絡您的單位管理員。
          </p>
        </>
      )}
      <div className="mt-6 flex gap-2">
        {isGuest ? (
          <Link to={loginHref} className="btn-primary text-sm inline-flex items-center gap-1.5">
            <LogIn className="h-4 w-4" /> 立即登入
          </Link>
        ) : (
          <Link to="/dashboard" className="btn-primary text-sm">回到首頁</Link>
        )}
        <a href="mailto:support@sentry.red" className="btn-ghost text-sm">聯絡客服</a>
      </div>
    </div>
  );
}
